import { i18n } from '@input-web/configs/locale/locale.config';
import { SubjectId } from '@master/constants/subject.constant';
import { StandardDetailGenerationStatus } from '@standard/constants/standard.constant';
import { getValueFromEnum } from '@util/utils/object.util';
import format from 'date-fns/format';
import ja from 'date-fns/locale/ja';
import parseISO from 'date-fns/parseISO';
import { TFunction } from 'i18next';
import {
  QUESTION_PARAGRAPH_INDENTATION,
  QUESTION_PARAGRAPH_SEPARATOR_CAPTURING_REGEX,
  QUESTION_PARAGRAPH_SEPARATOR_REGEX,
  QUESTION_SENTENCE_SEPARATOR_CAPTURING_REGEX,
  QUESTION_SENTENCE_SEPARATOR_REGEX,
  STANDARD_DETAIL_COLOR_MAX_NUMBER,
  STANDARD_DETAIL_COLOR_NUMBER,
  StandardDetailColorNumber,
} from '../constants/standard.constant';

// #region Types
export type StandardNumberedText = {
  number?: number;
  text: string;
};
// #endregion

/**
 * Converts the status of the question scoring document to a label
 *
 * @param status The status of the question scoring document
 * @param t i18next TFunction
 * @returns The label for the status of the question scoring document
 */
export function getStandardDetailStatusLabel(
  status?: StandardDetailGenerationStatus,
  t: TFunction = i18n.t,
) {
  return getValueFromEnum(
    status,
    {
      [StandardDetailGenerationStatus.NotGenerated]:
        t?.('standard.label.question-input-status.not-created') ?? '',
      [StandardDetailGenerationStatus.Generated]:
        t?.('standard.label.question-input-status.created') ?? '',
      [StandardDetailGenerationStatus.Generating]:
        t?.('standard.label.question-input-status.generating') ?? '',
      [StandardDetailGenerationStatus.GenerationError]:
        t?.('standard.label.question-input-status.need-to-recreate') ?? '',
    },
    '',
  );
}

/**
 * Get the text orientation from the subject id
 *
 * @param subjectId The subject id
 * @returns The text orientation from the subject id
 */
export function getTextOrientationStyleFromSubjectId(subjectId?: SubjectId | null) {
  return getValueFromEnum(subjectId, {
    [SubjectId.Japanese]: {
      msWritingMode: 'vertical-rl' as const,
      textOrientation: 'mixed' as const,
      WebkitTextOrientation: 'mixed' as const,
      WebkitWritingMode: 'vertical-rl' as const,
      writingMode: 'vertical-rl' as const,
    },
    [SubjectId.English]: {},
  });
}

/**
 * Add number to the texts
 *
 * @param texts The texts
 * @returns The texts with number
 */
export function addNumberToTexts(texts: string[]) {
  let number = 0;

  return texts.map((text) => {
    const hasNumber = !!text.trim();
    if (hasNumber) number += 1;

    return { ...(hasNumber && { number }), text } satisfies StandardNumberedText;
  });
}

/**
 * Split the text into groups
 *
 * @param text The text
 * @param groupRegex The regex to group the text
 * @param matchRegex The regex to match the text
 * @param onConcat The function to concatenate the text
 * @returns The text split into groups
 */
function splitTextIntermediary(
  text: string | null | undefined,
  groupRegex: RegExp,
  matchRegex: RegExp,
  onConcat: (text: string) => string = (value) => value,
) {
  return text?.split(groupRegex).reduce<string[]>((items, item, index) => {
    const currentItems = [...items];

    let shouldConcate = index > 0;
    shouldConcate &&= matchRegex.test(item);
    if (shouldConcate) {
      currentItems[currentItems.length - 1] = onConcat(
        `${currentItems[currentItems.length - 1]}${item}`,
      );
    } else currentItems.push(item);

    return currentItems;
  }, []);
}

/**
 * Split the question text into paragraphs
 *
 * @param questionText The question text
 * @param withIndentation Whether to include the indentation in the paragraphs
 * @returns The paragraphs of the question text without empty strings
 */
export function splitQuestionParagraphs(questionText?: string | null, withIndentation = true) {
  return splitTextIntermediary(
    questionText,
    QUESTION_PARAGRAPH_SEPARATOR_CAPTURING_REGEX,
    QUESTION_PARAGRAPH_SEPARATOR_REGEX,
    (text) => {
      let newText = text;
      const testText = newText;

      let shouldAddIndentation = withIndentation;
      shouldAddIndentation &&= !!testText.trim();
      shouldAddIndentation &&= !newText.startsWith(QUESTION_PARAGRAPH_INDENTATION);
      if (shouldAddIndentation) newText = `${QUESTION_PARAGRAPH_INDENTATION}${newText}`;

      return newText;
    },
  );
}

/**
 * Remove the indentation of the question text
 *
 * @param questionText The question text
 * @returns The question text without indentation
 */
export function removeQuestionParagraphIndentations(questionText?: string | null) {
  return splitTextIntermediary(
    questionText,
    QUESTION_PARAGRAPH_SEPARATOR_CAPTURING_REGEX,
    QUESTION_PARAGRAPH_SEPARATOR_REGEX,
    (text) => {
      let newText = text;
      const testText = newText;

      let shouldRemoveIndentation = !!testText.trim();
      shouldRemoveIndentation &&= newText.startsWith(QUESTION_PARAGRAPH_INDENTATION);
      if (shouldRemoveIndentation) newText = newText.slice(1);

      return newText;
    },
  )?.join('');
}

/**
 * Split the question text into paragraphs with number
 *
 * @param questionText The question text
 * @param withIndentation Whether to include the indentation in the paragraphs
 * @returns The paragraphs of the question text with number
 */
export function splitQuestionParagraphsWithNumber(
  questionText?: string | null,
  withIndentation = true,
) {
  const texts = splitQuestionParagraphs(questionText, withIndentation);

  return texts && addNumberToTexts(texts);
}

/**
 * Split the sentence text into sentences
 *
 * @param sentenceText The sentence text
 * @returns The sentences of the sentence text without empty strings
 */
export function splitQuestionSentences(sentenceText?: string | null) {
  return splitTextIntermediary(
    sentenceText,
    QUESTION_SENTENCE_SEPARATOR_CAPTURING_REGEX,
    QUESTION_SENTENCE_SEPARATOR_REGEX,
  );
}

export function splitQuestionSentencesWithNumber(sentenceText?: string | null) {
  const texts = splitQuestionSentences(sentenceText);

  return texts && addNumberToTexts(texts);
}

/**
 * Split the question text into paragraphs and sentences
 *
 * @param questionText The question text
 * @param withIndentation Whether to include the indentation in the paragraphs
 * @returns The paragraphs and sentences of the question text without empty strings
 */
export function splitQuestionParagraphsAndSentences(
  questionText?: string | null,
  withIndentation = true,
) {
  return splitQuestionParagraphs(questionText, withIndentation)?.map(
    (paragraph) => splitQuestionSentences(paragraph) ?? [],
  );
}

/**
 * Split the question text into paragraphs and sentences with number
 *
 * @param questionText The question text
 * @param withIndentation Whether to include the indentation in the paragraphs
 * @returns The paragraphs and sentences of the question text with number
 */
export function splitQuestionParagraphsAndSentencesWithNumber(
  questionText?: string | null,
  withIndentation = true,
) {
  return splitQuestionParagraphsWithNumber(questionText, withIndentation)?.map((paragraph) => ({
    number: paragraph.number,
    sentences: splitQuestionSentencesWithNumber(paragraph.text) ?? [],
  }));
}

/**
 * Format the updated at
 *
 * @param updatedAt The updated at
 * @returns The formatted updated at
 */
export function formatStandardUpdatedAt(updatedAt?: string | null | Date) {
  return (
    (updatedAt &&
      format(updatedAt instanceof Date ? updatedAt : parseISO(updatedAt), 'YoModo HH:mm', {
        locale: ja,
      })) ||
    ''
  );
}

/**
 * Get the color number for the value
 *
 * @param value The value to get the color number for
 * @returns The color number for the value
 */
export function getStandardDetailColorNumber(value?: number | null) {
  if (value == null) {
    return STANDARD_DETAIL_COLOR_NUMBER[STANDARD_DETAIL_COLOR_MAX_NUMBER - 1];
  }

  return (Math.abs(value) % STANDARD_DETAIL_COLOR_MAX_NUMBER) as StandardDetailColorNumber;
}

/**
 * Check if the standard form is changed
 *
 * @param checkers The checkers
 * @returns Whether the standard form is changed
 */
export function isStandardFormChanged(...checkers: (() => boolean)[]) {
  for (let index = 0; index < checkers.length; index += 1) {
    if (typeof checkers[index] === 'function' && checkers[index]()) return true;
  }

  return false;
}

export function convertFullToHalfTextCharacter(str: string): string;
export function convertFullToHalfTextCharacter(str?: string | null): string | null | undefined;
/**
 * Convert the full width characters to half width characters
 *
 * @param str The string to convert
 * @returns The string with half width characters
 */
export function convertFullToHalfTextCharacter(str?: string | null) {
  return str?.replace(/[！-～]/g, (character) =>
    String.fromCharCode(character.charCodeAt(0) - 0xfee0),
  );
}
